import React, { useState } from 'react';
import I18n from 'i18n-js';
import { RCButton, RCIcon, RCToast } from '@reverbdotcom/cadence/components';
import { HandshakeIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing, NextBestAction } from '@reverbdotcom/commons/src/gql/graphql';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';

import AutoOfferModal, { AUTO_OFFER_ACTION } from '../listings/auto_offer_modal';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import {
  Insight,
  trackFollowed,
  trackClicked,
  useTrackingContext,
  useTrackImpression,
} from './sellerRecommendationTracking';

interface Props {
  listing: Listing;
  nextBestAction: NextBestAction;
  insight: Insight;
}

export const COMPONENT_NAME = 'NBADirectOffersPresentment';

export default function NBADirectOffersPresentment({ listing, nextBestAction, insight }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const trackingContext = useTrackingContext();

  const details = {
    recommendedPriceCents: listing.priceRecommendation?.priceMiddle?.amountCents ?? undefined,
  };

  useTrackImpression(COMPONENT_NAME, insight, listing.id, details);

  const {
    useLazySellerListingsQuery,
  } = React.useContext(SellerListingsCollectionContext);
  const { refetchSellerListings } = useLazySellerListingsQuery();

  return (
    <>
      <div className="seller-listing-insight-presentment">
        <span className="seller-listing-insight-presentment__direct-offers">
          <RCIcon icon={<HandshakeIcon />} size="inline-prominent" spacing="right" color="accent-loud" />
          <RCTextUtility size="350" weight="bold">
            <SanitizedRender html={nextBestAction.title ?? ''} />
          </RCTextUtility>
        </span>
        <RCTextUtility size="350">
          <SanitizedRender html={nextBestAction.subtitle ?? ''} />
        </RCTextUtility>
        <RCButton
          variant="outlined"
          size="small"
          onClick={() => {
            trackClicked(COMPONENT_NAME, insight, listing.id, trackingContext, details);
            setIsModalOpen(true);
          }}
          fullWidth
        >
          {nextBestAction.ctaText}
        </RCButton>
      </div>

      {isModalOpen && (
        <AutoOfferModal
          listingId={listing.id}
          modalIsOpen={isModalOpen}
          onModalClose={() => setIsModalOpen(false)}
          refetchSellerListing={(action, appliedOfferPercent) => {
            // Removing an existing auto offer is the opposite of the recommendation,
            // so only a create counts as following it.
            if (action === AUTO_OFFER_ACTION.CREATED) {
              trackFollowed(COMPONENT_NAME, insight, listing.id, trackingContext, {
                ...details,
                appliedOfferPercent,
              });
            }
            setShowToast(true);
            refetchSellerListings([listing.id]);
          }}
        />
      )}

      <RCToast
        isOpen={showToast}
        onClose={() => setShowToast(false)}
        text={I18n.t('discovery.listingsManagement.toast.message.success')}
        theme="success"
        delayInterval="short"
      />
    </>
  );
}
